/**
 * Prep Test Store - Zustand
 * Sparar beredskapstestresultat och genererar personliga rekommendationer
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiGet, apiPost, apiPut } from '@/services/api-client';
import { logPrepTestAction } from '@/lib/activity-logger';

// Beredskapskategorier (matchar frågornas area i OnboardingPage)
export type PrepArea = 
  | 'water' 
  | 'food' 
  | 'energy' 
  | 'health' 
  | 'information' 
  | 'shelter' 
  | 'transport';

export type Priority = 'high' | 'medium' | 'low';

export interface AreaScore {
  area: PrepArea;
  score: number; // 0-100
  answeredQuestions: number;
  maxQuestions: number;
}

export interface Recommendation {
  id: string;
  area: PrepArea;
  priority: Priority;
  title: string;
  description: string;
  actionUrl?: string;
  completed: boolean;
}

interface PrepTestState {
  // Testresultat
  overallScore: number | null;
  areaScores: AreaScore[];
  testTakenAt: string | null;
  rawAnswers: Record<string, number>;
  
  // Rekommendationer baserade på testet
  recommendations: Recommendation[];
  
  // Actions
  saveTestResults: (answers: Record<string, number>) => void;
  generateRecommendations: () => void;
  markRecommendationComplete: (id: string, completed?: boolean) => Promise<void>;
  resetTest: () => Promise<void>;
  
  // Helpers
  getAreaScore: (area: PrepArea) => number;
  getPriorityAreas: () => PrepArea[];
  hasCompletedTest: () => boolean;
  
  // API Sync
  fetchFromServer: (familyId?: string) => Promise<void>;
  saveToServer: () => Promise<void>;
  
  // Hydration
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

// Mappa frågors ID till område
const questionAreaMap: Record<string, PrepArea> = {
  'water': 'water',
  'food': 'food',
  'cooking': 'food',
  'light': 'energy',
  'power': 'energy',
  'firstaid': 'health',
  'knowledge': 'health',
  'radio': 'information',
  'warmth': 'shelter',
  'transport': 'transport',
};

// Rekommendationer per område och nivå (med artikellänkar)
const recommendationTemplates: Record<PrepArea, { low: Array<{ text: string; articleId: string }>; medium: Array<{ text: string; articleId: string }>; high: Array<{ text: string; articleId: string }> }> = {
  water: {
    low: [
      { text: 'Köp 5-liters dunkar och fyll med kranvatten', articleId: 'water-1' },
      { text: 'Skaffa minst 35 liter vatten per person (7 dagars förråd)', articleId: 'water-1' },
      { text: 'Förvara vatten mörkt och svalt', articleId: 'water-1' },
    ],
    medium: [
      { text: 'Utöka till 14 dagars vattenförråd', articleId: 'water-1' },
      { text: 'Skaffa vattenreningstabletter som backup', articleId: 'water-2' },
    ],
    high: [
      { text: 'Överväg ett vattenfilter för långvariga kriser', articleId: 'water-2' },
      { text: 'Rotera vattnet var 6:e månad', articleId: 'water-1' },
    ],
  },
  food: {
    low: [
      { text: 'Börja med konserver och torrvaror för 7 dagar', articleId: 'food-1' },
      { text: 'Skaffa ett gasolkök eller stormkök', articleId: 'food-3' },
      { text: 'Öva på att laga mat utan el', articleId: 'food-3' },
    ],
    medium: [
      { text: 'Utöka matförrådet till 14-30 dagar', articleId: 'food-1' },
      { text: 'Lägg till varierad kost: müsli, nötter, torkad frukt', articleId: 'food-2' },
    ],
    high: [
      { text: 'Rotera matförrådet enligt "först in, först ut"', articleId: 'food-1' },
      { text: 'Lär dig att bevara och torka mat', articleId: 'food-2' },
    ],
  },
  energy: {
    low: [
      { text: 'Köp pannlampa och ficklampa med extra batterier', articleId: 'energy-2' },
      { text: 'Skaffa en powerbank (minst 10000 mAh)', articleId: 'energy-1' },
      { text: 'Ha stearinljus och tändstickor hemma', articleId: 'energy-2' },
    ],
    medium: [
      { text: 'Investera i en större batteribank eller solcellsladdare', articleId: 'energy-1' },
      { text: 'Skaffa vevficklampa som backup', articleId: 'energy-2' },
    ],
    high: [
      { text: 'Överväg en bärbar kraftstation för längre avbrott', articleId: 'energy-1' },
      { text: 'Se till att alla lampor har laddade batterier', articleId: 'energy-2' },
    ],
  },
  health: {
    low: [
      { text: 'Köp en komplett första hjälpen-väska', articleId: 'health-2' },
      { text: 'Gå en HLR-kurs (ofta gratis via jobbet)', articleId: 'health-1' },
      { text: 'Lär dig stoppa allvarlig blödning', articleId: 'health-1' },
    ],
    medium: [
      { text: 'Komplettera med tourniquet och Israeli bandage', articleId: 'health-2' },
      { text: 'Ha receptbelagda mediciner för minst 2 veckor', articleId: 'health-3' },
    ],
    high: [
      { text: 'Gå en fördjupad första hjälpen-kurs', articleId: 'health-1' },
      { text: 'Håll kunskaperna färska med årlig repetition', articleId: 'health-1' },
    ],
  },
  information: {
    low: [
      { text: 'Köp en batteridriven radio (DAB+/FM)', articleId: 'info-1' },
      { text: 'Lär dig frekvensen för Sveriges Radio P4', articleId: 'info-1' },
      { text: 'Ha papper och penna för att anteckna VMA', articleId: 'info-2' },
    ],
    medium: [
      { text: 'Skaffa vevradio eller solcellsradio', articleId: 'info-1' },
      { text: 'Ladda ner offlinekartor på mobilen', articleId: 'info-3' },
    ],
    high: [
      { text: 'Testa radion regelbundet', articleId: 'info-1' },
      { text: 'Ha en kommunikationsplan med familjen', articleId: 'info-2' },
    ],
  },
  shelter: {
    low: [
      { text: 'Samla extra filtar och varma kläder', articleId: 'shelter-1' },
      { text: 'Skaffa sovsäckar (komfort -5°C eller lägre)', articleId: 'shelter-3' },
      { text: 'Täta fönster och dörrar mot drag', articleId: 'shelter-1' },
    ],
    medium: [
      { text: 'Överväg reservvärme: gasolkamin eller etanolkamin', articleId: 'shelter-2' },
      { text: 'Ha ett rum som kan hållas varmt', articleId: 'shelter-2' },
    ],
    high: [
      { text: 'Om du har braskamin - ha ved för minst en vecka', articleId: 'shelter-2' },
      { text: 'Testa din reservvärme innan du behöver den', articleId: 'shelter-2' },
    ],
  },
  transport: {
    low: [
      { text: 'Bestäm en mötesplats om familjen separeras', articleId: 'comm-4' },
      { text: 'Packa en enkel evakueringsväska ("bug out bag")', articleId: 'comm-4' },
      { text: 'Ha alltid minst halvtankat fordon', articleId: 'comm-4' },
    ],
    medium: [
      { text: 'Planera två evakueringsvägar från hemmet', articleId: 'comm-4' },
      { text: 'Ha kontanter i olika valörer hemma', articleId: 'comm-4' },
    ],
    high: [
      { text: 'Öva evakueringsplanen med familjen', articleId: 'comm-4' },
      { text: 'Ha offlinekartor och kompass', articleId: 'comm-2' },
    ],
  },
};

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

export const usePrepTestStore = create<PrepTestState>()(
  persist(
    (set, get) => ({
      overallScore: null,
      areaScores: [],
      testTakenAt: null,
      rawAnswers: {},
      recommendations: [],
      
      saveTestResults: (answers) => {
        // Beräkna poäng per område
        const areaPoints: Record<PrepArea, { total: number; count: number }> = {
          water: { total: 0, count: 0 },
          food: { total: 0, count: 0 },
          energy: { total: 0, count: 0 },
          health: { total: 0, count: 0 },
          information: { total: 0, count: 0 },
          shelter: { total: 0, count: 0 },
          transport: { total: 0, count: 0 },
        };
        
        // Summera poäng per område
        Object.entries(answers).forEach(([questionId, score]) => {
          const area = questionAreaMap[questionId];
          if (area) {
            areaPoints[area].total += score;
            areaPoints[area].count += 1;
          }
        });
        
        // Beräkna genomsnitt per område
        const areaScores: AreaScore[] = Object.entries(areaPoints)
          .filter(([_, data]) => data.count > 0)
          .map(([area, data]) => ({
            area: area as PrepArea,
            score: Math.round(data.total / data.count),
            answeredQuestions: data.count,
            maxQuestions: data.count,
          }));
        
        // Beräkna total poäng
        const totalPoints = Object.values(answers).reduce((sum, val) => sum + val, 0);
        const maxPoints = Object.keys(answers).length * 100;
        const overallScore = Math.round((totalPoints / maxPoints) * 100);
        
        set({
          rawAnswers: answers,
          areaScores,
          overallScore,
          testTakenAt: new Date().toISOString(),
        });
        
        logPrepTestAction('COMPLETE_PREP_TEST', { overallScore });
        
        // Generera rekommendationer automatiskt
        get().generateRecommendations();
        
        // Spara till server asynkront
        get().saveToServer().catch(console.error);
      },
      
      generateRecommendations: () => {
        const { areaScores } = get();
        const recommendations: Recommendation[] = [];
        
        areaScores.forEach(({ area, score }) => {
          let templates: Array<{ text: string; articleId: string }>;
          let priority: Priority;
          
          if (score < 40) {
            templates = recommendationTemplates[area].low;
            priority = 'high';
          } else if (score < 70) {
            templates = recommendationTemplates[area].medium;
            priority = 'medium';
          } else {
            templates = recommendationTemplates[area].high;
            priority = 'low';
          }
          
          // Lägg till 1-2 rekommendationer per område
          const numRecs = priority === 'high' ? 2 : 1;
          templates.slice(0, numRecs).forEach(({ text }) => {
            // Mappa area till knowledge page area key
            const areaMapping: Record<PrepArea, string> = {
              water: 'water',
              food: 'food',
              energy: 'energy',
              health: 'health',
              information: 'information',
              shelter: 'shelter',
              transport: 'communication', // transport rekommendationer länkar till communication
            };
            
            const knowledgeAreaKey = areaMapping[area];
            
            recommendations.push({
              id: generateId(),
              area,
              priority,
              title: getAreaTitle(area),
              description: text,
              actionUrl: `/app/knowledge?area=${knowledgeAreaKey}`,
              completed: false,
            });
          });
        });
        
        // Sortera efter prioritet
        const priorityOrder: Record<Priority, number> = { high: 0, medium: 1, low: 2 };
        recommendations.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
        
        set({ recommendations });
      },
      
      markRecommendationComplete: async (id, completed) => {
        // If completed is not specified, toggle the current state
        const currentRec = get().recommendations.find(r => r.id === id);
        const newCompletedState = completed !== undefined ? completed : !currentRec?.completed;
        
        // Optimistic update
        set((state) => ({
          recommendations: state.recommendations.map((rec) =>
            rec.id === id ? { ...rec, completed: newCompletedState } : rec
          ),
        }));
        
        // Save to server using the dedicated endpoint
        try {
          const result = await apiPut(`/prep-test/recommendation/${id}`, { completed: newCompletedState });
          if (result.success) {
            logPrepTestAction('UPDATE_PREP_RECOMMENDATION', { recommendationId: id, completed: newCompletedState });
          } else {
            console.error('Failed to mark recommendation as complete:', result.error);
            // Rollback on failure
            set((state) => ({
              recommendations: state.recommendations.map((rec) =>
                rec.id === id ? { ...rec, completed: !newCompletedState } : rec
              ),
            }));
          }
        } catch (error) {
          console.error('Error marking recommendation as complete:', error);
          // Rollback on error
          set((state) => ({
            recommendations: state.recommendations.map((rec) =>
              rec.id === id ? { ...rec, completed: !newCompletedState } : rec
            ),
          }));
        }
      },
      
      resetTest: async () => {
        // Rensa local state
        set({
          overallScore: null,
          areaScores: [],
          testTakenAt: null,
          rawAnswers: {},
          recommendations: [],
        });
        
        // Spara den rensade datan till servern så att rekommendationer försvinner även där
        try {
          await apiPost('/prep-test', {
            overallScore: null,
            areaScores: [],
            rawAnswers: {},
            recommendations: [],
            testTakenAt: null,
          });
        } catch (error) {
          console.error('Failed to reset prep test on server:', error);
        }
      },
      
      getAreaScore: (area) => {
        const { areaScores } = get();
        const areaScore = areaScores.find((s) => s.area === area);
        return areaScore?.score ?? 0;
      },
      
      getPriorityAreas: () => {
        const { areaScores } = get();
        return areaScores
          .filter((s) => s.score < 50)
          .sort((a, b) => a.score - b.score)
          .map((s) => s.area);
      },
      
      hasCompletedTest: () => {
        const { testTakenAt } = get();
        return testTakenAt !== null;
      },
      
      fetchFromServer: async (familyId?: string) => {
        try {
          const endpoint = familyId 
            ? `/prep-test?familyId=${encodeURIComponent(familyId)}`
            : '/prep-test';
          
          const result = await apiGet<{ prepTest: any }>(endpoint);
          
          if (result.success && result.data?.prepTest) {
            const prepTest = result.data.prepTest;
            set({
              overallScore: prepTest.overallScore,
              areaScores: prepTest.areaScores,
              testTakenAt: prepTest.testTakenAt,
              rawAnswers: prepTest.rawAnswers,
              recommendations: prepTest.recommendations || [],
            });
          }
        } catch (error) {
          console.error('Error fetching prep test from server:', error);
        }
      },
      
      saveToServer: async () => {
        try {
          const state = get();
          
          // Hoppa över om inget test har tagits
          if (!state.testTakenAt) return;
          
          const payload = {
            overallScore: state.overallScore,
            areaScores: state.areaScores,
            rawAnswers: state.rawAnswers,
            recommendations: state.recommendations,
            testTakenAt: state.testTakenAt,
          };
          
          const result = await apiPost('/prep-test', payload);
          
          if (!result.success) {
            console.error('Failed to save prep test to server:', result.error);
          }
        } catch (error) {
          console.error('Error saving prep test to server:', error);
        }
      },
      
      _hasHydrated: false,
      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: 'beredskapshubben-prep-test',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        overallScore: state.overallScore,
        areaScores: state.areaScores,
        testTakenAt: state.testTakenAt,
        rawAnswers: state.rawAnswers,
        recommendations: state.recommendations,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// Hjälpfunktion för områdesnamn
function getAreaTitle(area: PrepArea): string {
  const titles: Record<PrepArea, string> = {
    water: 'Vatten',
    food: 'Mat & Matlagning',
    energy: 'Energi & Belysning',
    health: 'Hälsa & Första hjälpen',
    information: 'Information & Kommunikation',
    shelter: 'Skydd & Värme',
    transport: 'Transport & Evakuering',
  };
  return titles[area];
}

// Export helper hooks
export const useHasCompletedPrepTest = () => usePrepTestStore((state) => state.hasCompletedTest());
export const usePrepTestScore = () => usePrepTestStore((state) => state.overallScore);
export const usePriorityRecommendations = () => 
  usePrepTestStore((state) => state.recommendations.filter((r) => !r.completed).slice(0, 5));
